/**
 * AssessmentResponsePage
 *
 * Displays the QuestionnaireResponse associated with an assessment (Task) from the
 * client chart Measures tab. Shows title, dates, author/provider, status, and the
 * response body. Loads Task by taskId, resolves output to QuestionnaireResponse and
 * optionally the Questionnaire for display title.
 *
 * When the Task is `requested` (no QR yet), renders the Questionnaire as a fillable
 * form so the provider can complete it on behalf of the client ("Take Now" flow).
 */

import {
  ActionIcon,
  Alert,
  Box,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Group,
  Loader,
  ScrollArea,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { useQuestionnaireForm } from '@medplum/react-hooks';
import type { MedplumClient } from '@medplum/core';
import { formatHumanName } from '@medplum/core';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import { getLinkedTaskId, setPortalTaskReference } from '@unify/fhir-model/resources/questionnaire-response';
import { executeTaskMutation } from '../../lib/taskLifecycleBots';
import type {
  Observation,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { IconAlertCircle, IconArrowLeft, IconShield } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { PreviewItemArray } from '../settings/template-library/components/TemplatePreviewDrawer';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from '../../lib/caseloadFhirConstants';
import { getPatientAccountRefs } from '../../lib/clientFacingTasks';
import {
  resolveQuestionnaireVariables,
  useVariableContext,
} from '../../lib/template-variables';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getItemAnswerText } from '../appointments/fhirNoteMapping';
import {
  getQuestionnaireIdFromQuestionnaireResponse,
  getQuestionnaireResponseIdFromTask,
  matchObservationForQuestionnaireResponse,
  QR_DETAILED_STATUS_EXTENSION,
  resolveAssessmentScore,
  resolveInterpretationLabel,
  resolveMaxScoreForQuestionnaire,
} from '../../lib/assessmentMedplumAdapters';
import { getStatusColor, formatStatusText } from '../../lib/statusUtils';
import {
  validateRequiredTemplateFields,
  validateRequiredTemplateFieldsInScope,
} from '../../lib/questionnaireResponseFillValidation';
import { scrollToAndFlashQuestionnaireItem } from '../appointments/utils/scrollToQuestionnaireItem';

function formatDateTime(isoString: string | undefined): string {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoString;
  }
}

function formatDate(isoString: string | undefined): string {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleDateString('en-US', { dateStyle: 'medium' });
  } catch {
    return isoString;
  }
}

/** Resolve a FHIR Reference to a human-readable name (prefer Reference.display; else read resource). */
async function resolveReferenceToDisplayName(
  medplum: MedplumClient,
  ref: Reference | undefined,
): Promise<string> {
  if (!ref) return '—';
  const fromDisplay = ref.display?.trim();
  const r = ref.reference;
  if (!r) return fromDisplay || '—';
  const parts = r.split('/');
  if (parts.length < 2) return fromDisplay || r;
  const resourceType = parts[parts.length - 2];
  const id = parts[parts.length - 1];
  if (!resourceType || !id) return fromDisplay || r;

  if (resourceType !== 'Practitioner' && fromDisplay) return fromDisplay;

  try {
    switch (resourceType) {
      case 'Patient': {
        const p = await medplum.readResource('Patient', id);
        return (p.name?.[0] && formatHumanName(p.name[0])) || p.id || id;
      }
      case 'Practitioner': {
        const p = await medplum.readResource('Practitioner', id);
        return formatPractitionerStaffLabel(p) || p.id || id;
      }
      case 'RelatedPerson': {
        const p = await medplum.readResource('RelatedPerson', id);
        return (p.name?.[0] && formatHumanName(p.name[0])) || p.id || id;
      }
      case 'Organization': {
        const o = await medplum.readResource('Organization', id);
        return o.name ?? o.id ?? id;
      }
      default:
        return fromDisplay || id;
    }
  } catch {
    return fromDisplay || id;
  }
}

/** Renders questionnaire response items with labels (valueString) shown; prefers valueString over valueInteger. */
function AssessmentResponseItems({
  questionnaireResponse,
}: {
  questionnaireResponse: QuestionnaireResponse;
}): JSX.Element {
  const items = questionnaireResponse.item ?? [];
  return (
    <Stack gap="md">
      {items.map((item) => (
        <ResponseItemRow key={item.linkId ?? item.text ?? ''} item={item} />
      ))}
    </Stack>
  );
}

function ResponseItemRow({ item }: { item: QuestionnaireResponseItem }): JSX.Element {
  const questionText = item.text ?? '';
  const answerText = getItemAnswerText(item);
  return (
    <Box>
      <Text size="sm" fw={600} c="dark.8" mb={4}>
        {questionText}
      </Text>
      <Text size="sm" c="gray.7">
        {answerText || '—'}
      </Text>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Provider "Take Now" fill form — renders the Questionnaire for completion.
// ---------------------------------------------------------------------------

interface ProviderFillFormProps {
  questionnaire: Questionnaire;
  task: Task;
  patientId: string;
  onCompleted: () => void;
  onCancel: () => void;
}

function ProviderFillForm({
  questionnaire,
  task,
  patientId,
  onCompleted,
  onCancel,
}: ProviderFillFormProps): JSX.Element {
  const medplum = useMedplum();
  const [submitting, setSubmitting] = useState(false);

  const { context } = useVariableContext();
  const resolved = resolveQuestionnaireVariables(questionnaire, context, {
    onUnresolved: 'placeholder',
  });

  const formState = useQuestionnaireForm({
    questionnaire: resolved,
    disablePagination: false,
  });

  const handleSubmit = useCallback(async () => {
    if (formState.loading) return;

    const fieldValidation = validateRequiredTemplateFields(
      resolved,
      formState.questionnaireResponse,
    );
    if (!fieldValidation.success) {
      scrollToAndFlashQuestionnaireItem(fieldValidation.missingItemPath);
      notifications.show({
        color: 'yellow',
        title: 'Required fields missing',
        message: fieldValidation.message,
      });
      return;
    }

    setSubmitting(true);
    try {
      const qr = formState.questionnaireResponse;
      const canonicalRef =
        questionnaire.url && questionnaire.version
          ? `${questionnaire.url}|${questionnaire.version}`
          : `Questionnaire/${questionnaire.id}`;

      const episodeExt = task.extension?.find(
        (e) => e.url === EPISODE_OF_CARE_CONTEXT_EXTENSION,
      );
      const qrExtensions = [...(qr.extension ?? [])];
      if (episodeExt) qrExtensions.push(episodeExt);
      // Authoritative lifecycle state per PRD base model
      qrExtensions.push({ url: QR_DETAILED_STATUS_EXTENSION, valueCode: 'completed' });

      const accountRefs = await getPatientAccountRefs(medplum, patientId).catch(() => [] as Reference[]);

      // Build a plain reference to avoid FHIR type widening incompatibility between
      // Task.requester (includes Device | Organization) and QR.source (no Device/Org).
      const requesterRef = task.requester?.reference
        ? { reference: task.requester.reference, display: task.requester.display }
        : undefined;

      const qrDraft: QuestionnaireResponse = {
        ...qr,
        status: 'completed',
        authored: new Date().toISOString(),
        subject: { reference: `Patient/${patientId}` },
        questionnaire: canonicalRef,
        author: requesterRef,
        source: requesterRef,
        extension: qrExtensions,
        meta: {
          ...(accountRefs.length > 0 ? { accounts: accountRefs } : {}),
        },
      };
      if (task.id) {
        setPortalTaskReference(qrDraft, { reference: `Task/${task.id}` });
      }
      const created = await medplum.createResource<QuestionnaireResponse>(qrDraft);

      await executeTaskMutation(medplum, {
        action: 'complete-assessment',
        taskId: task.id,
        questionnaireResponseId: created.id,
      });

      notifications.show({
        color: 'green',
        title: 'Assessment submitted',
        message: `${questionnaire.title ?? 'Assessment'} has been completed.`,
      });
      onCompleted();
    } catch (err) {
      console.error('Failed to submit assessment:', err);
      notifications.show({
        color: 'red',
        title: 'Submission failed',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  }, [formState, questionnaire, resolved, task, patientId, medplum, onCompleted]);

  const handleNextPage = useCallback(() => {
    if ('loading' in formState && formState.loading) {
      return;
    }
    const pageValidation = validateRequiredTemplateFieldsInScope(
      resolved,
      formState.questionnaireResponse,
      formState.items,
    );
    if (!pageValidation.success) {
      scrollToAndFlashQuestionnaireItem(pageValidation.missingItemPath);
      notifications.show({
        color: 'yellow',
        title: 'Required fields missing',
        message: pageValidation.message,
      });
      return;
    }
    if (!('onNextPage' in formState)) {
      return;
    }
    formState.onNextPage();
  }, [formState, resolved]);

  if (formState.loading) {
    return <Group justify="center" py="xl"><Loader /></Group>;
  }

  if (formState.items.length === 0) {
    return <Text size="sm" c="dimmed">This assessment has no questions.</Text>;
  }

  if (formState.pagination) {
    const { pages, activePage, onPrevPage } = formState;
    const isLastPage = activePage >= pages.length - 1;

    return (
      <form onSubmit={(e) => { e.preventDefault(); if (isLastPage) handleSubmit(); }}>
        <Stack gap="md">
          <Stepper active={activePage} allowNextStepsSelect={false} size="sm">
            {pages.map((page, pageIndex) => (
              <Stepper.Step key={page.linkId} label={page.title}>
                {pageIndex === activePage && (
                  <PreviewItemArray
                    formState={formState}
                    context={[]}
                    items={formState.items}
                    responseItems={formState.responseItems}
                    questionnaireItems={resolved.item}
                  />
                )}
              </Stepper.Step>
            ))}
          </Stepper>
          <Group justify="center" gap="sm" mt="md">
            {activePage > 0 && (
              <Button variant="default" onClick={onPrevPage}>Back</Button>
            )}
            {!isLastPage && (
              <Button type="button" onClick={handleNextPage}>Next</Button>
            )}
            {isLastPage && (
              <Button type="submit" loading={submitting}>Submit assessment</Button>
            )}
          </Group>
        </Stack>
      </form>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
      <Stack gap="md">
        <PreviewItemArray
          formState={formState}
          context={[]}
          items={formState.items}
          responseItems={formState.responseItems}
          questionnaireItems={resolved.item}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onCancel}>Cancel</Button>
          <Button type="submit" loading={submitting}>Submit assessment</Button>
        </Group>
      </Stack>
    </form>
  );
}

type AssessmentPageMode = 'view' | 'take-now';

type AssessmentRouteState = {
  mode?: string;
  assessmentOnly?: boolean;
};

export function AssessmentResponsePage(): JSX.Element {
  const { id: clientId, taskId } = useParams<{ id: string; taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const medplum = useMedplum();
  const routeState = (location.state as AssessmentRouteState | null) ?? null;
  const assessmentOnly = routeState?.assessmentOnly === true;
  const routeMode: AssessmentPageMode =
    routeState?.mode === 'take-now' && !assessmentOnly ? 'take-now' : 'view';
  const [localMode, setLocalMode] = useState<AssessmentPageMode>(routeMode);

  const [task, setTask] = useState<Task | null>(null);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<QuestionnaireResponse | null>(
    null
  );
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [scoreObservation, setScoreObservation] = useState<Observation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Resolved labels for QuestionnaireResponse.author, subject / Task.for, and Task.requester */
  const [partyLabels, setPartyLabels] = useState({
    author: '—',
    client: '—',
    assigner: '—',
  });

  const backUrl = clientId ? `/dashboard/clients/${clientId}?tab=measures` : '/dashboard/caseload';

  const handleBack = useCallback(() => {
    navigate(backUrl);
  }, [navigate, backUrl]);

  const breadcrumbNav = (
    <Group gap="xs" align="center">
      <ActionIcon variant="subtle" color="gold.7" size="lg" onClick={handleBack} aria-label="Go back">
        <IconArrowLeft size={18} />
      </ActionIcon>
      <Breadcrumbs>
        <Text component={Link} to="/dashboard/caseload" c="gold.7" size="sm" fw={500}>Caseload</Text>
        {clientId && (
          <Text component={Link} to={`/dashboard/clients/${clientId}`} c="gold.7" size="sm" fw={500}>Client</Text>
        )}
        <Text component={Link} to={backUrl} c="gold.7" size="sm" fw={500}>Measures</Text>
        <Text size="sm" c="gray.6">Assessment</Text>
      </Breadcrumbs>
    </Group>
  );

  useEffect(() => {
    if (!taskId) {
      setError('Assessment not specified');
      setLoading(false);
      return;
    }

    if (taskId.startsWith('dummy-')) {
      setError('Assessment not found');
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Legacy chart rows used `qr-{questionnaireResponseId}` in the URL; load QR first, then Task from basedOn if present.
        if (taskId.startsWith('qr-')) {
          const qrLogicalId = taskId.slice('qr-'.length);
          if (!qrLogicalId) {
            if (!cancelled) setError('Invalid assessment link');
            return;
          }
          const qr = await medplum.readResource('QuestionnaireResponse', qrLogicalId);
          if (cancelled) return;
          setQuestionnaireResponse(qr);

          const basedOnTask = getLinkedTaskId(qr);
          if (basedOnTask) {
            const tid = basedOnTask;
            if (tid) {
              try {
                const t = await medplum.readResource('Task', tid);
                if (!cancelled) setTask(t);
              } catch {
                if (!cancelled) setTask(null);
              }
            } else if (!cancelled) {
              setTask(null);
            }
          } else if (!cancelled) {
            setTask(null);
          }

          const qId = getQuestionnaireIdFromQuestionnaireResponse(qr);
          if (qId) {
            try {
              const q = await medplum.readResource('Questionnaire', qId);
              if (!cancelled) setQuestionnaire(q);
            } catch {
              if (!cancelled) setQuestionnaire(null);
            }
          } else if (!cancelled) {
            setQuestionnaire(null);
          }
          return;
        }

        const t = await medplum.readResource('Task', taskId);
        if (cancelled) return;
        setTask(t);

        const qrId = getQuestionnaireResponseIdFromTask(t);
        if (!qrId) {
          setQuestionnaireResponse(null);
          // No QR yet — load Questionnaire from Task.focus for fill mode
          const focusRef = t.focus?.reference;
          if (focusRef?.startsWith('Questionnaire/')) {
            try {
              const qId = focusRef.split('/')[1];
              const q = await medplum.readResource('Questionnaire', qId);
              if (!cancelled) setQuestionnaire(q);
            } catch {
              if (!cancelled) setQuestionnaire(null);
            }
          } else {
            setQuestionnaire(null);
          }
          if (!cancelled) setLoading(false);
          return;
        }

        const qr = await medplum.readResource('QuestionnaireResponse', qrId);
        if (cancelled) return;
        setQuestionnaireResponse(qr);

        const qId = getQuestionnaireIdFromQuestionnaireResponse(qr);
        if (qId) {
          try {
            const q = await medplum.readResource('Questionnaire', qId);
            if (!cancelled) setQuestionnaire(q);
          } catch {
            if (!cancelled) setQuestionnaire(null);
          }
        } else {
          setQuestionnaire(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load assessment response');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, taskId]);

  useEffect(() => {
    if (!questionnaireResponse?.id || !clientId) {
      setScoreObservation(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const obsList = await medplum.searchResources('Observation', {
          subject: `Patient/${clientId}`,
        });
        if (cancelled) return;
        const matched = matchObservationForQuestionnaireResponse(
          obsList,
          questionnaireResponse,
          questionnaire ?? undefined,
        );
        setScoreObservation(matched ?? null);
      } catch {
        if (!cancelled) setScoreObservation(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, clientId, questionnaireResponse, questionnaire]);

  useEffect(() => {
    if (!questionnaireResponse && !task) {
      setPartyLabels({ author: '—', client: '—', assigner: '—' });
      return;
    }
    let cancelled = false;
    void (async () => {
      const qr = questionnaireResponse;
      const t = task;
      const [author, client, assigner] = await Promise.all([
        resolveReferenceToDisplayName(medplum, qr?.author),
        resolveReferenceToDisplayName(medplum, qr?.subject ?? t?.for),
        resolveReferenceToDisplayName(medplum, t?.requester),
      ]);
      if (!cancelled) {
        setPartyLabels({ author, client, assigner });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, questionnaireResponse, task]);

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          {breadcrumbNav}
          <Alert icon={<IconAlertCircle size={20} />} color="red" variant="light" title="Error">
            {error}
          </Alert>
        </Stack>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          {breadcrumbNav}
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        </Stack>
      </Container>
    );
  }

  // Task loaded but no QuestionnaireResponse
  if (task && !questionnaireResponse) {
    const fillTitle = questionnaire?.title ?? questionnaire?.name ?? task.description ?? 'Assessment';

    if (localMode === 'take-now' && questionnaire && !assessmentOnly) {
      return (
        <Container size="xl" py="xl">
          <Stack gap="xl">
            {breadcrumbNav}
            <div>
              <Title order={3}>{fillTitle}</Title>
              <Text size="sm" c="gray.6" mt={4}>
                Complete this assessment on behalf of the client.
              </Text>
            </div>
            <Card withBorder p="lg">
              <ProviderFillForm
                questionnaire={questionnaire}
                task={task}
                patientId={clientId!}
                onCompleted={handleBack}
                onCancel={handleBack}
              />
            </Card>
          </Stack>
        </Container>
      );
    }

    return (
      <Container size="xl" py="xl">
        <Stack gap="xl">
          {breadcrumbNav}
          <div>
            <Title order={3}>{fillTitle}</Title>
            <Text size="sm" c="gray.6" mt={4}>
              Assessment response
            </Text>
          </div>
          {assessmentOnly && (
            <Alert icon={<IconShield size={18} />} color="gray" variant="light" title="Response unavailable">
              This assessment belongs to another episode of care. You can view assignment details here;
              questionnaire answers are only available to that episode’s care team.
            </Alert>
          )}
          {!assessmentOnly && (
            <>
              <Alert variant="light" color="blue" title="Not yet completed">
                This assessment has not been completed yet. The questionnaire response will appear here
                once the client submits it.
              </Alert>
              {questionnaire && (
                <Group>
                  <Button onClick={() => setLocalMode('take-now')}>
                    Take Now (complete on behalf of client)
                  </Button>
                </Group>
              )}
            </>
          )}
        </Stack>
      </Container>
    );
  }

  const displayTitle =
    questionnaire?.title ?? questionnaire?.name ?? task?.description ?? 'Assessment';
  const authoredDate = questionnaireResponse?.authored ?? task?.authoredOn ?? task?.lastModified;
  const taskStatus = task?.status ?? '—';

  const resolvedScore =
    questionnaireResponse && questionnaire
      ? resolveAssessmentScore(
          questionnaireResponse,
          questionnaire,
          scoreObservation ?? undefined,
        )
      : null;
  const interpretationLabel =
    questionnaireResponse && questionnaire && resolvedScore
      ? resolveInterpretationLabel({
          qr: questionnaireResponse,
          questionnaire,
          score: resolvedScore.score,
          observation: scoreObservation ?? undefined,
        })
      : undefined;
  const maxScoreDisplay = questionnaire ? resolveMaxScoreForQuestionnaire(questionnaire) : undefined;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {breadcrumbNav}

        <div>
          <Title order={3}>{displayTitle}</Title>
          <Text size="sm" c="gray.6" mt={4}>
            Assessment response
          </Text>
        </div>

        <Card withBorder p="md">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md" verticalSpacing="md">
            <div>
              <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                Completed
              </Text>
              <Text size="sm">{formatDateTime(authoredDate)}</Text>
            </div>
            {task?.authoredOn && (
              <div>
                <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                  Assigned
                </Text>
                <Text size="sm">{formatDate(task.authoredOn)}</Text>
              </div>
            )}
            <div>
              <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                Completed By
              </Text>
              <Text size="sm">{partyLabels.author}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                Client
              </Text>
              <Text size="sm">{partyLabels.client}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                Assigned By
              </Text>
              <Text size="sm">{partyLabels.assigner}</Text>
            </div>
            <div>
              <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                Status
              </Text>
              <Badge size="sm" variant="light" color={getStatusColor(taskStatus)}>
                {formatStatusText(taskStatus)}
              </Badge>
            </div>
            {resolvedScore && maxScoreDisplay != null && (
              <div>
                <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                  Score ({resolvedScore.source})
                </Text>
                <Text size="sm" fw={600}>
                  {resolvedScore.score}
                  <Text span c="gray.6" fw={400}>
                    {' '}
                    / {maxScoreDisplay}
                  </Text>
                </Text>
              </div>
            )}
            {interpretationLabel && (
              <div>
                <Text size="xs" c="gray.6" tt="uppercase" fw={600} mb={4}>
                  Interpretation
                </Text>
                <Text size="sm">{interpretationLabel}</Text>
              </div>
            )}
          </SimpleGrid>
        </Card>

        {assessmentOnly ? (
          <Alert icon={<IconShield size={18} />} color="gray" variant="light" title="Response unavailable">
            This assessment belongs to another episode of care. Summary details are shown above;
            questionnaire answers are only available to that episode’s care team.
          </Alert>
        ) : (
          <Card withBorder p="lg">
            <ScrollArea.Autosize mah="70vh" offsetScrollbars type="scroll">
              <Box pb="md" pr="xs">
                <AssessmentResponseItems questionnaireResponse={questionnaireResponse!} />
              </Box>
            </ScrollArea.Autosize>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
